import React, { useEffect, useState } from "react";
import { moduleApi } from "../../lib/api";
import { DEFAULT_TAKEN_BY_OPTIONS, DEFAULT_CASHED_BY_OPTIONS, DEFAULT_BOARD_COLUMNS, mapBoardFromApi, type Board } from "./types";
import type { BoardColumn } from "./boardColumns";
import { NewBoardModal } from "./NewBoardModal";
import { Dashboard } from "./Dashboard";
import { BoardDetail } from "./BoardDetail";
import { ArchivedBoards } from "./ArchivedBoards";
import { GlobalSummary } from "./GlobalSummary";
import { useConfirm } from '../ui/ConfirmProvider';

type View = "dashboard" | "detail" | "archives" | "global";

export const ExceptionServiceModule: React.FC = () => {
  const [view, setView] = useState<View>("dashboard");
  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);
  /** Tableau en cours de duplication : rouvre la modale de création, pré-remplie de son nom et de ses colonnes. */
  const [duplicateSource, setDuplicateSource] = useState<Board | null>(null);
  const confirmAction = useConfirm();

  const selectedBoard = boards.find((b) => b.id === selectedBoardId) ?? boards[0];
  const effectiveTakenByOptions = selectedBoard?.takenByOptions ?? DEFAULT_TAKEN_BY_OPTIONS;
  const effectiveCashedByOptions = selectedBoard?.cashedByOptions ?? DEFAULT_CASHED_BY_OPTIONS;

  useEffect(() => {
    (async () => {
      try {
        const data = await moduleApi.getExceptionServiceBoards();
        if (Array.isArray(data)) {
          const mapped = data.map(mapBoardFromApi);
          setBoards(mapped);
          if (mapped.length > 0) setSelectedBoardId(mapped[0].id);
        }
      } catch (e) {
        console.error("Failed to load boards", e);
      }
    })();
  }, []);

  const handleCreateBoard = async (title: string, serviceDate: string, reuseColumns: boolean, columnsOverride?: BoardColumn[]) => {
    try {
      // « Reprendre les colonnes du dernier tableau » = celles du tableau au service le plus récent, pas
      // du dernier créé : c'est celui dont on vient, et donc celui qu'on veut rejouer.
      const previous = [...boards].filter((b) => !b.isArchived)
        .sort((a, b) => a.serviceDate.localeCompare(b.serviceDate)).pop();
      const columns = columnsOverride ?? (reuseColumns && previous ? previous.columns : DEFAULT_BOARD_COLUMNS);

      const res: any = await moduleApi.createExceptionServiceBoard({
        title,
        service_date: serviceDate,
        columns,
        taken_by_options: DEFAULT_TAKEN_BY_OPTIONS,
        cashed_by_options: DEFAULT_CASHED_BY_OPTIONS,
      });
      const newBoard: Board = {
        id: res.id,
        title,
        serviceDate,
        createdAt: new Date().toLocaleDateString("fr-FR"),
        isArchived: false,
        takenByOptions: DEFAULT_TAKEN_BY_OPTIONS,
        cashedByOptions: DEFAULT_CASHED_BY_OPTIONS,
        columns,
      };
      setBoards((prev) => [...prev, newBoard]);
    } catch (e) {
      console.error("Failed to create board", e);
    }
  };

  const handleOpenBoard = (id: number) => {
    setSelectedBoardId(id);
    setView("detail");
  };

  const handleArchiveBoard = async (id: number) => {
    if (!(await confirmAction({ title: 'Archiver le tableau ?', description: <>Il ne sera plus modifiable et ne pourra plus être désarchivé.</>, confirmLabel: 'Archiver le tableau', destructive: false }))) return;
    try {
      await moduleApi.updateExceptionServiceBoard(id, { is_archived: true });
    } catch (e) {
      console.error("Failed to archive board", e);
    }
    setBoards((prev) => prev.map((b) => (b.id === id ? { ...b, isArchived: true } : b)));
  };

  const handleDeleteBoard = async (id: number, summary?: React.ReactNode) => {
    if (!(await confirmAction({
      title: 'Supprimer le tableau ?',
      description: summary ?? <>Le tableau et toutes ses réservations seront effacés définitivement.</>,
      confirmLabel: 'Supprimer le tableau',
    }))) return;
    try {
      await moduleApi.deleteExceptionServiceBoard(id);
    } catch (e) {
      console.error("Failed to delete board", e);
    }
    setBoards((prev) => {
      const next = prev.filter((b) => b.id !== id);
      if (selectedBoardId === id) {
        const first = next[0];
        setSelectedBoardId(first ? first.id : null);
        setView("dashboard");
      }
      return next;
    });
  };

  /**
   * Dupliquer : même nom, mêmes colonnes, AUCUNE réservation, et une date à choisir. Reprendre la date
   * d'origine créerait deux services le même jour — or on duplique justement pour l'édition suivante.
   */
  const handleDuplicateBoard = (id: number) => {
    const source = boards.find((b) => b.id === id);
    if (!source) return;
    setDuplicateSource(source);
  };

  const handleUpdateColumns = async (columns: BoardColumn[]) => {
    if (!selectedBoard) return;
    try {
      await moduleApi.updateExceptionServiceBoard(selectedBoard.id, { columns });
    } catch (e) {
      console.error("Failed to update columns", e);
    }
    setBoards((prev) => prev.map((b) => (b.id === selectedBoard.id ? { ...b, columns } : b)));
  };

  const handleUpdateSettings = async ({ title, takenByOptions, cashedByOptions }: { title: string; takenByOptions: string[]; cashedByOptions: string[] }) => {
    if (!selectedBoard) return;
    try {
      await moduleApi.updateExceptionServiceBoard(selectedBoard.id, {
        title,
        taken_by_options: takenByOptions,
        cashed_by_options: cashedByOptions,
      });
    } catch (e) {
      console.error("Failed to update board settings", e);
    }
    setBoards((prev) => prev.map((b) => (b.id === selectedBoard.id ? { ...b, title, takenByOptions, cashedByOptions } : b)));
  };

  if (view === "detail") {
    return (
      <BoardDetail
        onBack={() => setView("dashboard")}
        board={selectedBoard}
        takenByOptions={effectiveTakenByOptions}
        cashedByOptions={effectiveCashedByOptions}
        onUpdateSettings={handleUpdateSettings}
        onUpdateColumns={handleUpdateColumns}
        onDeleteBoard={handleDeleteBoard}
      />
    );
  }
  if (view === "archives") {
    return <ArchivedBoards boards={boards} onBack={() => setView("dashboard")} onOpenBoard={handleOpenBoard} />;
  }
  if (view === "global") {
    return <GlobalSummary boards={boards} onBack={() => setView("dashboard")} />;
  }
  return (
    <>
    <Dashboard
      boards={boards}
      onOpenBoard={handleOpenBoard}
      onOpenArchives={() => setView("archives")}
      onArchive={handleArchiveBoard}
      onCreateBoard={handleCreateBoard}
      onDuplicateBoard={handleDuplicateBoard}
      onOpenGlobal={() => setView("global")}
      onDeleteBoard={handleDeleteBoard}
    />
    {duplicateSource && (
      <NewBoardModal
        key={`dup-${duplicateSource.id}`}
        onClose={() => setDuplicateSource(null)}
        canReuseColumns={false}
        initialTitle={duplicateSource.title}
        initialColumns={duplicateSource.columns}
        onCreate={handleCreateBoard}
      />
    )}
    </>
  );
};
